/**
 * <Autocomplete> — multi-select search list (molecule, L0.1).
 *
 * Chips for selected catalog items + extras, a search `<TextInput>`, a vertical stack of
 * `<Card>` + `<Checkbox>` options, optional `groupBy` headers, loading, and an "Ajouter…"
 * CTA when `allowExtra`. Selection state is controlled by the parent; search query is local.
 * Pendant que les options arrivent, la liste en montre la forme — des lignes d'option, sous leur
 * propre balayage ; le champ de recherche attend, éteint.
 *
 * `selection="single"` en fait une liste à choix unique (un sport, un pays) : plus de chips ni de
 * cases, chaque option est une `<OptionCard>`. `onToggle` reçoit alors l'option choisie, et c'est
 * l'appelant qui remplace sa valeur. `searchable={false}` retire le champ de recherche d'une
 * liste courte.
 *
 * No middle list shell — options are a plain `<Stack>` (unlike horizontal selects, which need
 * `<HorizontalList>`). Domain forms (e.g. chapters) pass items + extras; they don't re-implement
 * filter / chips / create chrome.
 */
import React, { useMemo, useState } from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Badge, type BadgeVariant } from "../../atoms/Badge"
import { Checkbox } from "../../atoms/Checkbox"
import { CHECKBOX_GLYPH_SIZE } from "../../atoms/Checkbox/Checkbox"
import { Row } from "../../atoms/containers/Row"
import { DelayedSkeleton, Skeleton } from "../../atoms/Skeleton"
import { Icon, type IconName } from "../../atoms/icons/Icon"
import { TextInput } from "../../atoms/TextInput"
import { Stack } from "../../atoms/containers/Stack"
import { ErrorText } from "../../atoms/typography/ErrorText"
import { Text } from "../../atoms/typography/Text"
import { Button } from "../../atoms/Buttons/Button"
import { Card } from "../Card"
import { OptionCard } from "../OptionCard"

export type AutocompleteExtra = {
    id: string
    label: string
}

export type AutocompleteGroup = {
    id: string | number
    label: string
    /** Une chip à côté de l'intitulé du groupe — l'échéance « J-5 » d'un contrôle. Absente par défaut. */
    badge?: { text: string; variant?: BadgeVariant }
}

/**
 * Ce que la liste offre quand la recherche ne trouve rien : une ligne qui emporte le texte cherché
 * ailleurs (« Décrire « … » dans Instructions »), au lieu de laisser un champ muet.
 */
export type AutocompleteNoResultAction = {
    label: (query: string) => string
    /** La raison, sous le libellé. */
    hint?: string
    iconName?: IconName
    onPress: (query: string) => void
}

export type AutocompleteSelection = "multiple" | "single"

export type AutocompleteProps<T> = {
    label?: string
    /**
     * `"multiple"` (défaut) : chips + cases à cocher. `"single"` : une seule option à la fois, sans
     * chips — ne pas le combiner avec `extras` / `allowExtra`, un ajout libre n'y aurait ni
     * affichage ni retrait.
     */
    selection?: AutocompleteSelection
    /**
     * Champ de recherche au-dessus des options. Défaut `true`. À `false`, il n'y a plus de saisie :
     * `allowExtra` et `noResultAction`, qui partent du texte cherché, n'ont plus d'effet.
     */
    searchable?: boolean
    items: T[]
    getItemId: (item: T) => string | number
    getItemLabel: (item: T) => string
    selectedIds: Array<string | number>
    onToggle: (id: string | number) => void
    /** Free-form selections (e.g. custom chapter names). */
    extras?: AutocompleteExtra[]
    /** Badge variant for extras — defaults to `warning`. */
    extraVariant?: BadgeVariant
    onRemoveExtra?: (id: string) => void
    /** Show "Ajouter {query}" when the search box has text. */
    allowExtra?: boolean
    onAddExtra?: (query: string) => void
    /** Group options; `null` → ungrouped bucket ("Autres" when other groups exist). */
    groupBy?: (item: T) => AutocompleteGroup | null
    filterItem?: (item: T, query: string) => boolean
    /** L'issue d'une recherche sans résultat. Absente → rien ne s'affiche, comme avant. */
    noResultAction?: AutocompleteNoResultAction
    loading?: boolean
    disabled?: boolean
    searchPlaceholder?: string
    /** Ce qui manque ici, dit en rouge sous le libellé — au-dessus des options, qu'on voit en arrivant. */
    error?: string
    testID?: string
}

/** Les options qui arrivent : quatre lignes, de longueurs différentes comme des chapitres. */
const OPTION_BONE_WIDTHS = [190, 150, 210, 130]

export function Autocomplete<T>(props: AutocompleteProps<T>) {
    const {
        label,
        selection = "multiple",
        searchable = true,
        items,
        getItemId,
        getItemLabel,
        selectedIds,
        onToggle,
        extras = [],
        extraVariant = "warning",
        onRemoveExtra,
        allowExtra = false,
        onAddExtra,
        groupBy,
        filterItem,
        noResultAction,
        loading = false,
        disabled = false,
        searchPlaceholder = "Rechercher…",
        error,
        testID,
    } = props

    const [query, setQuery] = useState("")

    const selectedIdSet = useMemo(() => new Set(selectedIds.map(String)), [selectedIds])

    const selectedItems = useMemo(
        () => items.filter((item) => selectedIdSet.has(String(getItemId(item)))),
        [items, selectedIdSet, getItemId]
    )

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase()
        if (!q) return items
        if (filterItem != null) return items.filter((item) => filterItem(item, q))
        return items.filter((item) => getItemLabel(item).toLowerCase().includes(q))
    }, [items, query, filterItem, getItemLabel])

    const { groups, ungrouped } = useMemo(() => {
        if (groupBy == null) return { groups: [] as { group: AutocompleteGroup; items: T[] }[], ungrouped: filtered }

        const map = new Map<string, { group: AutocompleteGroup; items: T[] }>()
        const rest: T[] = []
        for (const item of filtered) {
            const g = groupBy(item)
            if (g == null) {
                rest.push(item)
                continue
            }
            const key = String(g.id)
            const bucket = map.get(key)
            if (bucket != null) bucket.items.push(item)
            else map.set(key, { group: g, items: [item] })
        }
        return { groups: [...map.values()], ungrouped: rest }
    }, [filtered, groupBy])

    const trimmed = query.trim()
    const showAddExtra = allowExtra && onAddExtra != null && trimmed.length > 0 && !disabled && !loading
    const showNoResultAction = noResultAction != null && trimmed.length > 0 && filtered.length === 0 && !disabled

    const renderOption = (item: T) => {
        const id = getItemId(item)
        const selected = selectedIdSet.has(String(id))
        if (selection === "single") {
            return (
                <OptionCard
                    key={String(id)}
                    label={getItemLabel(item)}
                    selected={selected}
                    disabled={disabled}
                    onPress={() => onToggle(id)}
                />
            )
        }
        return (
            <Card
                key={String(id)}
                background={selected ? "primary-soft" : "surface-subtle"}
                // Une ligne d'option est ENCASTRÉE dans la liste : dix ombres empilées feraient
                // lire la pile de boîtes à la place des options.
                elevation="none"
                p="3"
                style={styles.option(selected)}
            >
                <Checkbox
                    checked={selected}
                    onChange={() => onToggle(id)}
                    label={getItemLabel(item)}
                    disabled={disabled}
                />
            </Card>
        )
    }

    return (
        <Stack gap="3" testID={testID}>
            {label != null && (
                <Text size="text-base" weight="bold" color="primary">
                    {label}
                </Text>
            )}

            {error != null && error !== "" && <ErrorText live>{error}</ErrorText>}

            {selection === "multiple" && (selectedItems.length > 0 || extras.length > 0) && (
                <View style={styles.chips}>
                    {selectedItems.map((item) => {
                        const id = getItemId(item)
                        return (
                            <Badge
                                key={`sel-${String(id)}`}
                                text={getItemLabel(item)}
                                variant="primary"
                                onRemove={
                                    disabled
                                        ? undefined
                                        : () => {
                                              onToggle(id)
                                          }
                                }
                            />
                        )
                    })}
                    {extras.map((extra) => (
                        <Badge
                            key={`extra-${extra.id}`}
                            text={extra.label}
                            variant={extraVariant}
                            onRemove={disabled || onRemoveExtra == null ? undefined : () => onRemoveExtra(extra.id)}
                        />
                    ))}
                </View>
            )}

            {searchable && (
                <TextInput
                    value={query}
                    onChangeText={setQuery}
                    placeholder={searchPlaceholder}
                    disabled={disabled || loading}
                    left={<Icon name="search" size={14} color="text-muted" />}
                />
            )}

            {loading && (
                <DelayedSkeleton>
                    <Stack gap="2">
                        {OPTION_BONE_WIDTHS.map((width) => (
                            <Card
                                key={width}
                                background="surface-subtle"
                                elevation="none"
                                p="3"
                                style={styles.option(false)}
                            >
                                <Row gap="2" align="center">
                                    <Skeleton
                                        shape="block"
                                        width={CHECKBOX_GLYPH_SIZE}
                                        height={CHECKBOX_GLYPH_SIZE}
                                        radius="sm"
                                    />
                                    <Skeleton textSize="text-base" width={width} />
                                </Row>
                            </Card>
                        ))}
                    </Stack>
                </DelayedSkeleton>
            )}

            {!loading && (
                <Stack gap="3">
                    {groups.map(({ group, items: groupItems }) => (
                        <Stack key={String(group.id)} gap="2">
                            {group.badge != null ? (
                                <Row gap="2" align="center">
                                    <Text size="text-sm" weight="semibold" color="text-muted">
                                        {group.label}
                                    </Text>
                                    <Badge text={group.badge.text} variant={group.badge.variant ?? "neutral"} tinted />
                                </Row>
                            ) : (
                                <Text size="text-sm" weight="semibold" color="text-muted">
                                    {group.label}
                                </Text>
                            )}
                            <Stack gap="2">{groupItems.map(renderOption)}</Stack>
                        </Stack>
                    ))}

                    {ungrouped.length > 0 && (
                        <Stack gap="2">
                            {groups.length > 0 && (
                                <Text size="text-sm" weight="semibold" color="text-muted">
                                    Autres
                                </Text>
                            )}
                            {ungrouped.map(renderOption)}
                        </Stack>
                    )}

                    {showNoResultAction && (
                        <Card
                            background="surface-subtle"
                            elevation="none"
                            p="3"
                            pressable
                            onPress={() => {
                                noResultAction.onPress(trimmed)
                                setQuery("")
                            }}
                            style={styles.option(false)}
                        >
                            <Row gap="3" align="center">
                                <Icon name={noResultAction.iconName ?? "pencil-alt"} size={16} color="primary" />
                                <Stack gap="0" style={styles.noResultText}>
                                    <Text weight="semibold" color="primary">
                                        {noResultAction.label(trimmed)}
                                    </Text>
                                    {noResultAction.hint != null && (
                                        <Text size="text-sm" color="text-muted">
                                            {noResultAction.hint}
                                        </Text>
                                    )}
                                </Stack>
                            </Row>
                        </Card>
                    )}

                    {showAddExtra && (
                        <Button
                            label={`Ajouter "${trimmed}"`}
                            iconName="plus"
                            variant="outline"
                            size="sm"
                            onPress={() => {
                                onAddExtra(trimmed)
                                setQuery("")
                            }}
                            disabled={disabled}
                        />
                    )}
                </Stack>
            )}
        </Stack>
    )
}

const styles = StyleSheet.create((theme) => ({
    chips: {
        flexDirection: "row" as const,
        flexWrap: "wrap" as const,
        gap: theme.spacing["2"],
    },
    option: (selected: boolean) => ({
        borderColor: selected ? theme.colors.primary : theme.colors.border,
    }),
    noResultText: { flexShrink: 1, flexGrow: 1 },
}))
